import { FC, Fragment } from 'react'
import styles from './messageText.module.scss'

interface MessageTextProps {
  // Search term to mark inside the copy (case-insensitive, literal).
  highlight?: string
  text: string
}

const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Case-insensitive literal matcher for a search term; null for an empty term.
// Non-global so .test() is stateless (see IS_URL below for the same trap).
export const buildHighlightRegex = (term: string) => {
  const trimmed = term.trim()
  return trimmed ? new RegExp(escapeRegex(trimmed), 'i') : null
}

// Wraps every occurrence of `highlight` in <mark>; plain text when no term.
export const HighlightedText: FC<{ highlight?: string; text: string }> = ({
  highlight,
  text,
}) => {
  const trimmed = highlight?.trim()
  if (!trimmed) return <>{text}</>
  // Capturing group so split() keeps the matches as their own parts.
  const splitter = new RegExp(`(${escapeRegex(trimmed)})`, 'gi')
  // split() pulls every occurrence out as its own part, so a part that still
  // contains the term can only be one of those captures.
  const matcher = buildHighlightRegex(trimmed)
  return (
    <>
      {text.split(splitter).map((part, index) =>
        matcher && part && matcher.test(part) ? (
          // eslint-disable-next-line react/no-array-index-key
          <mark className={styles.highlight} key={`hl-${index}`}>
            {part}
          </mark>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={`t-${index}`}>{part}</Fragment>
        )
      )}
    </>
  )
}

// Capturing, so split() keeps the links as their own parts.
const URL_PATTERN = /(https?:\/\/[^\s]+)/g

// A separate, non-global copy: a /g regex carries lastIndex between calls, so
// testing the parts with URL_PATTERN itself would skip every other link.
const IS_URL = /^https?:\/\/[^\s]+$/

// Long enough that an ordinary repeated word ("ok ok") can never trigger the
// trim below — only a page title can realistically repeat at this length.
const MIN_ECHO_LENGTH = 12

// Guards the O(n²) scan. Messages this long are not link shares.
const MAX_SCANNED_LENGTH = 2000

/**
 * Drops the link-preview text that the Messenger scrape glues onto a shared
 * link. Meta renders the preview as a card, but reading the bubble's
 * textContent runs the card's copy into the message, so a link arrives as
 *
 *   …ბმულზე: https://stambahotel.com/cafe-stamba/Café Stamba …Hotelstambahotel.comCafé Stamba …Hotel
 *
 * The card's title appears twice — once under the link, once as the card's
 * own caption — so the echo is found as the longest suffix that also occurs
 * earlier. Everything from that first occurrence is the card, not the message.
 *
 * Two guards keep real messages intact: the echo has to start inside the URL's
 * own token (no whitespace between the link and the repeat), and it has to be
 * long enough that a repeated word cannot trigger it. A preview whose title
 * appears only once is left alone rather than guessed at.
 */
export const stripLinkPreviewEcho = (text: string) => {
  if (!text || text.length > MAX_SCANNED_LENGTH) {
    return text
  }

  const urlStart = text.search(/https?:\/\//)
  if (urlStart === -1) {
    return text
  }

  const tail = text.slice(urlStart)

  for (
    let length = Math.floor(tail.length / 2);
    length >= MIN_ECHO_LENGTH;
    length--
  ) {
    const suffix = tail.slice(tail.length - length)
    const firstIndex = tail.indexOf(suffix)

    // Four conditions, and all of them matter:
    //  • the occurrences must not overlap;
    //  • the repeat is glued to the link — no whitespace between them — which
    //    is what separates a preview card from the sender's own repetition
    //    ("let me know, let me know");
    //  • it may not open with a space, for the same reason;
    //  • it must contain a space somewhere. A page title reads like prose,
    //    while a repeat with no spaces at all is part of the URL itself:
    //    a Facebook redirect embeds its destination, so fragments repeat
    //    inside one perfectly valid link. Without this the link is truncated.
    if (
      firstIndex > 0 &&
      firstIndex + length <= tail.length - length &&
      !/^\s/.test(suffix) &&
      /\s/.test(suffix) &&
      !/\s/.test(tail.slice(0, firstIndex))
    ) {
      return text.slice(0, urlStart + firstIndex).trimEnd()
    }
  }

  return text
}

// Renders a message body: the copy as written, with any links clickable.
export const MessageText: FC<MessageTextProps> = ({ highlight, text }) => {
  const cleaned = stripLinkPreviewEcho(text)

  return (
    <>
      {cleaned.split(URL_PATTERN).map((part, index) =>
        IS_URL.test(part) ? (
          <a
            className={styles.link}
            href={part}
            // eslint-disable-next-line react/no-array-index-key
            key={`${part}-${index}`}
            // The bubble sits inside a scrollable modal, so the click must not
            // travel further than the anchor itself.
            onClick={(event) => event.stopPropagation()}
            rel="noopener noreferrer"
            target="_blank"
          >
            {part}
          </a>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={`text-${index}`}>
            <HighlightedText highlight={highlight} text={part} />
          </Fragment>
        )
      )}
    </>
  )
}
